// Photoshop blend modes used by the compiler (JS) and renderer (GLSL).
// Codes are shared with the shader in renderer.js and cpuRenderer.js.

export const BLEND_CODES = {
  normal: 0,
  multiply: 1,
  screen: 2,
  overlay: 3,
  darken: 4,
  lighten: 5,
  "color dodge": 6,
  "color burn": 7,
  "hard light": 8,
  "soft light": 9,
  difference: 10,
  exclusion: 11,
  "linear burn": 12,
  "linear dodge": 13,
  "linear light": 14,
  "vivid light": 15,
  "pin light": 16,
  subtract: 17,
  divide: 18,
  hue: 19,
  saturation: 20,
  color: 21,
  luminosity: 22,
};

const ALIASES = { dissolve: "normal", "pass through": "normal", "darker color": "darken", "lighter color": "lighten", "hard mix": "vivid light" };

/** Normalizes an ag-psd blend mode name. Unknown modes fall back to normal. */
export function blendName(mode) {
  const name = String(mode || "normal").toLowerCase();
  if (name in BLEND_CODES) return name;
  return ALIASES[name] || "normal";
}

export const isApproximatedAlias = (mode) => {
  const name = String(mode || "normal").toLowerCase();
  return !(name in BLEND_CODES) && name !== "pass through" && name !== "dissolve";
};

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Separable blend B(cb, cs) for one channel in [0, 1]. */
export function blendChannel(code, cb, cs) {
  switch (code) {
    case 1: return cb * cs;
    case 2: return cb + cs - cb * cs;
    case 3: return cb <= 0.5 ? 2 * cb * cs : 1 - 2 * (1 - cb) * (1 - cs);
    case 4: return Math.min(cb, cs);
    case 5: return Math.max(cb, cs);
    case 6: return cb === 0 ? 0 : cs >= 1 ? 1 : Math.min(1, cb / (1 - cs));
    case 7: return cb >= 1 ? 1 : cs <= 0 ? 0 : 1 - Math.min(1, (1 - cb) / cs);
    case 8: return cs <= 0.5 ? 2 * cb * cs : 1 - 2 * (1 - cb) * (1 - cs);
    case 9: {
      if (cs <= 0.5) return cb - (1 - 2 * cs) * cb * (1 - cb);
      const d = cb <= 0.25 ? ((16 * cb - 12) * cb + 4) * cb : Math.sqrt(cb);
      return cb + (2 * cs - 1) * (d - cb);
    }
    case 10: return Math.abs(cb - cs);
    case 11: return cb + cs - 2 * cb * cs;
    case 12: return clamp01(cb + cs - 1);
    case 13: return clamp01(cb + cs);
    case 14: return clamp01(cb + 2 * cs - 1);
    case 15:
      if (cs <= 0.5) return cs <= 0 ? 0 : clamp01(1 - (1 - cb) / (2 * cs));
      return cs >= 1 ? 1 : clamp01(cb / (2 * (1 - cs)));
    case 16: return cs <= 0.5 ? Math.min(cb, 2 * cs) : Math.max(cb, 2 * cs - 1);
    case 17: return clamp01(cb - cs);
    case 18: return cs <= 0 ? 1 : clamp01(cb / cs);
    default: return cs;
  }
}

// Non-separable modes (W3C compositing spec definitions).
const lum = (r, g, b) => 0.3 * r + 0.59 * g + 0.11 * b;
function clipColor(c) {
  const l = lum(c[0], c[1], c[2]), n = Math.min(c[0], c[1], c[2]), x = Math.max(c[0], c[1], c[2]);
  if (n < 0) for (let i = 0; i < 3; i++) c[i] = l + ((c[i] - l) * l) / (l - n || 1e-6);
  if (x > 1) for (let i = 0; i < 3; i++) c[i] = l + ((c[i] - l) * (1 - l)) / (x - l || 1e-6);
  return c;
}
function setLum(c, l) {
  const d = l - lum(c[0], c[1], c[2]);
  return clipColor([c[0] + d, c[1] + d, c[2] + d]);
}
const sat = (c) => Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]);
function setSat(c, s) {
  const out = [0, 0, 0];
  const idx = [0, 1, 2].sort((a, b) => c[a] - c[b]);
  const [mn, md, mx] = idx;
  if (c[mx] > c[mn]) {
    out[md] = ((c[md] - c[mn]) * s) / (c[mx] - c[mn]);
    out[mx] = s;
  }
  return out;
}

/** Writes B(cb, cs) for all three channels into `out`. */
export function blendPixel(code, cb, cs, out) {
  if (code < 19) {
    out[0] = blendChannel(code, cb[0], cs[0]);
    out[1] = blendChannel(code, cb[1], cs[1]);
    out[2] = blendChannel(code, cb[2], cs[2]);
    return out;
  }
  let r;
  if (code === 19) r = setLum(setSat(cs, sat(cb)), lum(cb[0], cb[1], cb[2]));
  else if (code === 20) r = setLum(setSat(cb, sat(cs)), lum(cb[0], cb[1], cb[2]));
  else if (code === 21) r = setLum(cs, lum(cb[0], cb[1], cb[2]));
  else r = setLum(cb, lum(cs[0], cs[1], cs[2]));
  out[0] = r[0];
  out[1] = r[1];
  out[2] = r[2];
  return out;
}

/**
 * Decomposes "apply layer (color s, alpha a) with blend `code`" as an affine
 * function of the backdrop channel d: result ≈ d * gain + bias.
 * Exact for modes in AFFINE_EXACT; others use their unclamped affine form or
 * a secant through d = 0.25 and d = 0.75, and are reported as approximated.
 */
export function affineForChannel(code, s, a, out = [0, 0]) {
  let k, c;
  switch (code) {
    case 0: k = 0; c = s; break;
    case 1: k = s; c = 0; break;
    case 2: k = 1 - s; c = s; break;
    case 8: if (s <= 0.5) { k = 2 * s; c = 0; } else { k = 2 - 2 * s; c = 2 * s - 1; } break;
    case 11: k = 1 - 2 * s; c = s; break;
    case 12: k = 1; c = s - 1; break;
    case 13: k = 1; c = s; break;
    case 14: k = 1; c = 2 * s - 1; break;
    case 17: k = 1; c = -s; break;
    default: {
      const f0 = blendChannel(code, 0.25, s), f1 = blendChannel(code, 0.75, s);
      k = (f1 - f0) / 0.5;
      c = f0 - 0.25 * k;
    }
  }
  out[0] = 1 - a + a * k;
  out[1] = a * c;
  return out;
}

// Modes whose per-layer result is affine in the backdrop. Linear burn/dodge,
// linear light and subtract also clamp per layer, so their accumulated form
// is exact only while results stay within [0, 1].
export const AFFINE_EXACT = new Set([0, 1, 2, 8, 11]);
